/**
 * Shared API Client
 * Core fetch wrapper with retry logic, timeout, and error mapping.
 * All feature-specific API modules import from here.
 */

import { getContext, captureError } from "@/shared/lib/observability";

// Base URL for proxy
export const PROXY_BASE = "/api/proxy";

/**
 * Observability headers attached to every outgoing fetch. The proxy
 * forwards these to the backend so logs on both sides share a trace key.
 *
 *   X-Request-ID — page-load trace key from PageContext (browser only)
 *   X-Session-ID — PostHog session id when available
 *
 * SSR fetches (no window) skip these — the proxy synthesizes a fresh
 * X-Request-ID server-side in that case.
 */
function observabilityHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const ctx = getContext();
  const headers: Record<string, string> = { "X-Request-ID": ctx.requestId };
  if (ctx.sessionId) headers["X-Session-ID"] = ctx.sessionId;
  return headers;
}

// Retry configuration — exponential backoff with jitter
const BASE_DELAYS = [0, 500, 2_000, 8_000];
const addJitter = (delay: number): number =>
  delay + Math.random() * delay * 0.3;

// Timeout configuration
export const DEFAULT_TIMEOUT = 60_000; // 60 seconds for normal requests
export const AI_TIMEOUT = 120_000; // 2 minutes for AI operations

// Backward-compatible aliases
export const RETRY_DELAYS = BASE_DELAYS;
export const REQUEST_TIMEOUT = DEFAULT_TIMEOUT;

// ── Simple Circuit Breaker ─────────────────────────────────────────────
const CIRCUIT_FAILURE_THRESHOLD = 5;
const CIRCUIT_COOLDOWN_MS = 30_000; // 30 seconds

let consecutiveFailures = 0;
let circuitOpenUntil = 0;

function isCircuitOpen(): boolean {
  if (Date.now() >= circuitOpenUntil) {
    // Circuit has cooled down — reset
    if (circuitOpenUntil > 0) {
      consecutiveFailures = 0;
      circuitOpenUntil = 0;
    }
    return false;
  }
  return true;
}

function recordSuccess(): void {
  consecutiveFailures = 0;
}

function recordFailure(): void {
  consecutiveFailures++;
  if (consecutiveFailures >= CIRCUIT_FAILURE_THRESHOLD) {
    circuitOpenUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    console.warn(
      `[API] Circuit breaker OPEN — ${consecutiveFailures} consecutive failures. Cooling down for ${CIRCUIT_COOLDOWN_MS / 1000}s.`
    );
  }
}

/**
 * Error messages mapping from Swagger spec
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: "Validation Error",
  401: "Unauthorized",
  402: "Insufficient Credits",
  403: "Forbidden",
  404: "Not Found",
  500: "Internal Server Error",
  503: "Service Unavailable",
};

export function getErrorMessage(status: number): string {
  return ERROR_MESSAGES[status] || "Request failed";
}

/** API error shape */
export interface ApiErrorResponse {
  success: false;
  message: string;
  error: string;
  statusCode: number;
  requiredCredits?: unknown;
  availableCredits?: unknown;
  featureCode?: unknown;
}

/**
 * Build error response object
 */
export function buildErrorResponse<T>(
  statusCode: number,
  data: Record<string, unknown>
): T {
  const errorResponse: ApiErrorResponse = {
    success: false,
    message: (data.message as string) || (data.error as string) || getErrorMessage(statusCode),
    error: (data.error as string) || `HTTP ${statusCode}`,
    statusCode,
  };

  // Include additional fields for insufficient credits (402)
  if (statusCode === 402 && data.requiredCredits !== undefined) {
    return {
      ...errorResponse,
      requiredCredits: data.requiredCredits,
      availableCredits: data.availableCredits,
      featureCode: data.featureCode,
    } as T;
  }

  return errorResponse as T;
}

/**
 * Safely read a response body as a plain object.
 *
 * Backend/proxy errors don't always return JSON: nginx emits an HTML page on
 * 502/504 gateway timeouts, and a 413 returns a plain-text "Request Entity Too
 * Large". Calling `response.json()` on those throws
 * `SyntaxError: Unexpected token '<'`, which the catch block then mis-reports as
 * a network error — triggering pointless retries against an already-overloaded
 * backend and showing the user a cryptic crash instead of "try again".
 *
 * This reads the body as text and parses JSON only when it actually parses. For
 * non-JSON bodies it synthesizes a minimal error object keyed off the HTTP
 * status, so buildErrorResponse() can still produce a clean, user-facing message.
 */
export async function parseResponseBody(
  response: Response
): Promise<Record<string, unknown>> {
  const raw = await response.text();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : { message: String(parsed) };
  } catch {
    // Non-JSON body (HTML gateway error page, plain-text 413, etc.). Never
    // surface the raw HTML to the user — map by status code instead.
    return { error: getErrorMessage(response.status) };
  }
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Options for apiFetch beyond standard RequestInit.
 */
export interface ApiFetchOptions extends RequestInit {
  /** Override the per-request timeout (ms). Defaults to DEFAULT_TIMEOUT. */
  timeout?: number;
}

/**
 * Generic fetch wrapper with retry logic, timeout, and circuit breaker.
 * - Exponential backoff with jitter on retries
 * - Retries on 5xx errors and network failures
 * - Does NOT retry on 4xx errors (client errors)
 * - Circuit breaker opens after 5 consecutive failures (30s cooldown)
 */
export async function apiFetch<T>(
  endpoint: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  // Circuit breaker check
  if (isCircuitOpen()) {
    return {
      success: false,
      message: "Service temporarily unavailable — too many recent failures. Please try again shortly.",
      error: "CIRCUIT_OPEN",
      statusCode: 503,
    } as T;
  }

  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  let lastError: T | null = null;

  for (let attempt = 0; attempt < BASE_DELAYS.length; attempt++) {
    // Wait before retry (0ms for first attempt)
    if (attempt > 0) {
      const delay = addJitter(BASE_DELAYS[attempt]);
      console.warn(`[API] Retry ${attempt} for ${endpoint} after ${Math.round(delay)}ms`);
      await sleep(delay);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(endpoint, {
        ...fetchOptions,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...observabilityHeaders(),
          ...fetchOptions.headers,
        },
      });
      clearTimeout(timeoutId);

      const data = await parseResponseBody(response);

      if (response.ok) {
        recordSuccess();
        return data as T;
      }

      // Don't retry 4xx errors (client errors)
      if (response.status >= 400 && response.status < 500) {
        recordSuccess(); // 4xx is a valid server response, not a failure
        return buildErrorResponse<T>(response.status, data);
      }

      // 5xx errors - save and retry
      recordFailure();
      lastError = buildErrorResponse<T>(response.status, data);
    } catch (error) {
      clearTimeout(timeoutId);
      recordFailure();

      const isAbortError = error instanceof Error && error.name === "AbortError";
      lastError = {
        success: false,
        message: isAbortError ? "Request timed out" : "Network error",
        error: error instanceof Error ? error.message : "Unknown error",
        statusCode: 500,
      } as T;
    }
  }

  // All retries exhausted
  return lastError!;
}

/**
 * Fetch wrapper for multipart/form-data requests with retry logic.
 * Uses AI_TIMEOUT by default (form uploads typically involve AI processing).
 */
export async function apiFormFetch<T>(
  endpoint: string,
  formData: FormData,
  timeout: number = AI_TIMEOUT
): Promise<T> {
  // Circuit breaker check
  if (isCircuitOpen()) {
    return {
      success: false,
      message: "Service temporarily unavailable — too many recent failures. Please try again shortly.",
      error: "CIRCUIT_OPEN",
      statusCode: 503,
    } as T;
  }

  let lastError: T | null = null;


  for (let attempt = 0; attempt < BASE_DELAYS.length; attempt++) {
    if (attempt > 0) {
      const delay = addJitter(BASE_DELAYS[attempt]);
      console.warn(`[API] Retry ${attempt} for ${endpoint} after ${Math.round(delay)}ms`);
      await sleep(delay);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        signal: controller.signal,
        // Don't set Content-Type header - browser will set it with boundary
        headers: observabilityHeaders(),
      });
      clearTimeout(timeoutId);

      const data = await parseResponseBody(response);

      if (response.ok) {
        recordSuccess();
        return data as T;
      }

      // Don't retry 4xx errors
      if (response.status >= 400 && response.status < 500) {
        recordSuccess(); // 4xx is a valid server response
        console.error(`[API] Client error ${response.status}:`, data);
        return buildErrorResponse<T>(response.status, data);
      }

      recordFailure();
      console.warn(`[API] Server error ${response.status}, will retry`);
      lastError = buildErrorResponse<T>(response.status, data);
    } catch (error) {
      clearTimeout(timeoutId);
      recordFailure();

      const isAbortError = error instanceof Error && error.name === "AbortError";
      captureError(error, {
        feature: "shared:client",
        op: isAbortError ? "timeout" : "network-error",
      });
      lastError = {
        success: false,
        message: isAbortError ? "Request timed out" : "Network error",
        error: error instanceof Error ? error.message : "Unknown error",
        statusCode: 500,
      } as T;
    }
  }

  console.error(`[API] All retries exhausted for ${endpoint}`);
  return lastError!;
}

/**
 * Build URL with query parameters
 */
export function buildUrl(base: string, params?: Record<string, unknown>): string {
  if (!params) return base;

  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.pathname + url.search;
}
